/**
 * Sume de bani, ca **text**.
 *
 * Coloanele de bani sunt `DECIMAL(12,2)` și Drizzle le citește ca `string` — intenționat.
 * Regula care rezultă e simplă și n-are excepții: **suma nu trece niciodată prin
 * `number`**, nici la validare, nici la normalizare. Un `parseFloat('49.99')` întoarce
 * `49.990000000000002`, iar scris înapoi în coloană devine `50.00` la a treia rotunjire —
 * exact problema pe care tipul de coloană a eliminat-o.
 *
 * De aceea normalizarea de mai jos e o lucrare pe șiruri: se despart cifrele de separator,
 * se completează zecimalele, atât. Fără aritmetică.
 *
 * Vezi docs/wiki/architecture/mariadb-notes.md#bani
 */

/** Câte cifre încap înaintea virgulei într-un `DECIMAL(12,2)`. */
const MAX_INTEGER_DIGITS = 10;

/** Câte cifre încap după virgulă. Restul n-ar fi trunchiat, ci **rotunjit** de MariaDB. */
const MAX_FRACTION_DIGITS = 2;

/**
 * Suma tastată de operator → forma canonică `"49.99"`, sau `null` dacă nu e o sumă.
 *
 * Ce acceptă: virgulă sau punct ca separator zecimal (tastatura românească dă virgulă),
 * spații la capete, zecimale lipsă sau incomplete (`"50"` → `"50.00"`).
 *
 * Ce refuză, și de ce fiecare:
 * - **negativele** — un preț negativ nu e o reducere, e o greșeală de tastare; dacă
 *   vreodată apare un storno, e altă coloană, nu un semn minus aici;
 * - **peste două zecimale** — MariaDB ar rotunji tăcut, iar operatorul ar vedea altă sumă
 *   decât a scris;
 * - **separatorii de mii** — `"1.234,56"` și `"1,234.56"` sunt aceeași sumă în două
 *   convenții, iar ghicitul între ele produce erori de o mie de ori valoarea;
 * - **notația exponențială și `Infinity`** — sunt forme pe care doar un `parseFloat` le-ar
 *   fi înghițit.
 */
export function normalizeAmount(value: string): string | null {
  const trimmed = value.trim().replace(',', '.');

  if (trimmed.length === 0) {
    return null;
  }

  // Un singur separator, cifre în rest. Regex-ul e și apărarea împotriva lui `1e3`,
  // `-5`, `1.234,56` și a spațiilor din interior.
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);

  if (!match) {
    return null;
  }

  const [, digits = '', fraction = ''] = match;
  // Zerourile din față se taie, dar `"0"` și `"0.50"` rămân sume valide: `"000049"` și
  // `"49"` trebuie să dea același rând în bază, altfel `diffFields` raportează o
  // modificare între două valori identice.
  const integer = digits.replace(/^0+(?=\d)/, '');

  if (integer.length > MAX_INTEGER_DIGITS || fraction.length > MAX_FRACTION_DIGITS) {
    return null;
  }

  return `${integer}.${fraction.padEnd(MAX_FRACTION_DIGITS, '0')}`;
}

/**
 * Câte cifre are un curs înaintea virgulei: cel mult două — `4.9750`, nu `49750`.
 *
 * ⚠️ **E o plasă pentru tastare, nu o afirmație despre piață.** Aplicația are doar RON, EUR și
 * USD, iar un leu pe euro sau pe dolar e de ordinul unităților. Un curs scris zilnic, de mână,
 * fără punct (`49750`, `4975`) ar fi dat facturi de o mie sau de zece mii de ori mai mari;
 * refuzat aici, operatorul vede greșeala sub câmp.
 */
const MAX_RATE_INTEGER_DIGITS = 2;

/** Câte zecimale are un curs: un factor, nu o sumă — vezi docs/wiki/domains/currency.md */
const MAX_RATE_FRACTION_DIGITS = 6;

/**
 * Cursul tastat de operator (lei pentru o unitate de valută) → forma canonică `"4.9750"`, sau
 * `null` dacă nu e un curs.
 *
 * Aceeași regulă de separator ca la `normalizeAmount`: **un singur separator, și e cel
 * zecimal**. Virgula de pe tastatura românească devine punct — `"4,9750"` → `"4.9750"` —, iar
 * `"4.975"` rămâne patru virgulă nouă sute șaptezeci și cinci, nu patru mii: separator de mii
 * nu există, deci n-ai între ce ghici.
 *
 * Zecimalele rămân câte a scris operatorul (`4.9750` nu devine `4.975`): BNR publică patru, iar
 * mențiunea de pe factură tipărește cursul cum a fost dat.
 *
 * ⚠️ **Fără `parseFloat`, nicăieri.** `parseFloat("4,9750")` dă `4`: o factură cu 20% mai
 * ieftină, care arată perfect normal. Cursul se citește pe șiruri, iar înmulțirea se face pe
 * întregi, la emitere.
 *
 * Refuză: zero (un curs zero ar da facturi gratuite), negativele, mai mult de șase zecimale,
 * mai mult de două cifre întregi, separatorii de mii și tot ce n-ar fi înghițit decât un
 * `parseFloat`.
 */
export function normalizeExchangeRate(value: string): string | null {
  const trimmed = value.trim().replace(',', '.');
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);

  if (!match) {
    return null;
  }

  const [, digits = '', fraction] = match;
  const integer = digits.replace(/^0+(?=\d)/, '');

  if (
    integer.length > MAX_RATE_INTEGER_DIGITS ||
    (fraction !== undefined && fraction.length > MAX_RATE_FRACTION_DIGITS) ||
    !/[1-9]/.test(integer + (fraction ?? ''))
  ) {
    return null;
  }

  return fraction === undefined ? integer : `${integer}.${fraction}`;
}

/**
 * Monedele, cu codurile din `crm_subscriptions.currency` și `crm_packages.currency`.
 *
 * Numere, nu coduri ISO, pentru că așa vin din schema propusă de fondator și așa le scrie
 * sistemul actual. În ecrane, moneda e o etichetă lipită de sumă; conversia în lei se face doar
 * la facturare, cu cursul din setări (#185, `normalizeExchangeRate`).
 * Vezi docs/wiki/domains/currency.md
 */
export const CURRENCIES = {
  ron: 1,
  eur: 2,
  usd: 3,
} as const;

export type CurrencyCode = (typeof CURRENCIES)[keyof typeof CURRENCIES];

export const currencyCodes: readonly CurrencyCode[] = Object.values(CURRENCIES);

/** Codul ISO 4217, pentru `Intl.NumberFormat`. */
const ISO_BY_CODE: Record<CurrencyCode, string> = {
  [CURRENCIES.ron]: 'RON',
  [CURRENCIES.eur]: 'EUR',
  [CURRENCIES.usd]: 'USD',
};

export function currencyIsoCode(code: CurrencyCode): string {
  return ISO_BY_CODE[code];
}

/**
 * `TINYINT` din bază → una dintre monedele cunoscute.
 *
 * Coloana poate purta orice număr scris direct în bază de sistemul extern. Necunoscutul
 * devine `RON`, adică valoarea implicită a coloanei: e alegerea care nu poate face o sumă
 * să pară mai mare decât e.
 */
export function toCurrencyCode(value: number): CurrencyCode {
  return value === CURRENCIES.eur || value === CURRENCIES.usd ? value : CURRENCIES.ron;
}
